/**
 * Skin radius overlay — renders a flat circle at each selected vertex showing
 * the current per-vertex skin radius scale factor.
 *
 * Each circle is a flat RingGeometry billboard (always faces the camera), so
 * its appearance carries no rotation-axis implication. The radius maps directly
 * to the effective tube radius: skinRadii[v] × mod.depth.
 *
 * Rendered in object-local space (parent group carries the object transform).
 * Only shown when the skin_radius operator is active or a skinRadius command
 * is in progress.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";

// ---------------------------------------------------------------------------
// Geometry (shared singleton — flat unit ring, scaled per-instance)
// ---------------------------------------------------------------------------

let _ringGeo: THREE.RingGeometry | null = null;
function getRingGeometry(): THREE.RingGeometry {
  if (!_ringGeo) {
    // Thin flat annulus: inner = 92% of outer. At unit scale this looks like
    // a crisp circle outline with no depth or implied axis.
    _ringGeo = new THREE.RingGeometry(0.92, 1.0, 48);
  }
  return _ringGeo;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SkinRadiusOverlayProps {
  objectId: string;
  /** Resolved modifier list from the parent renderer (BlendModifier[] with overrides applied). */
  resolvedModifiers: BlendModifier[];
}

const _dummy = new THREE.Object3D();
const _camDir = new THREE.Vector3();
const _lookQuat = new THREE.Quaternion();
// RingGeometry lies in the XY plane; its face normal points along +Z.
const _ringNormal = new THREE.Vector3(0, 0, 1);

export function SkinRadiusOverlay({
  objectId,
  resolvedModifiers,
}: SkinRadiusOverlayProps) {
  const { camera } = useThree();

  const editMode = useStore((s) => s.editMode);
  const activeOperator = useStore((s) => s.activeOperator);
  const commandBuffer = useStore((s) => s.commandBuffer);
  const obj = useStore((s) => s.sceneObjects.get(objectId));

  const meshRef = useRef<THREE.InstancedMesh>(null);

  // Find skin modifier depth from the resolved modifier list
  const skinDepth = useMemo(() => {
    const skinMod = resolvedModifiers.find(
      (m): m is Extract<BlendModifier, { kind: "skin" }> => m.kind === "skin",
    );
    return skinMod?.depth ?? 0.1;
  }, [resolvedModifiers]);

  // Only render when skin_radius operator is active or command is running
  const isActive =
    activeOperator === "skin_radius" || commandBuffer?.type === "skinRadius";

  // Collect selected vertex positions and radii
  const instanceData = useMemo(() => {
    if (
      !isActive ||
      !editMode ||
      editMode.objectId !== objectId ||
      !obj ||
      editMode.isCurve
    ) {
      return null;
    }
    const selected = editMode.selectedVertices;
    if (selected.size === 0) return null;

    const verts = obj.vertices;
    const radii = obj.skinRadii;
    const positions: number[] = [];
    const scales: number[] = [];

    for (const vi of selected) {
      positions.push(verts[vi * 3], verts[vi * 3 + 1], verts[vi * 3 + 2]);
      scales.push(Math.max(1e-4, (radii ? radii[vi] : 1) * skinDepth));
    }

    return { positions, scales, count: selected.size };
  }, [isActive, editMode, objectId, obj, skinDepth]);

  // Update instanced mesh matrices each frame (billboard toward camera)
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh || !instanceData) return;

    // Orient flat ring face toward camera (ring normal aligns with camera direction)
    camera.getWorldDirection(_camDir);
    _lookQuat.setFromUnitVectors(_ringNormal, _camDir);

    const { positions, scales, count } = instanceData;
    for (let i = 0; i < count; i++) {
      const b = i * 3;
      _dummy.position.set(positions[b], positions[b + 1], positions[b + 2]);
      _dummy.quaternion.copy(_lookQuat);
      const s = scales[i];
      _dummy.scale.set(s, s, s);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: 0x44aaff,
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  if (!isActive || !instanceData) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[getRingGeometry(), material, instanceData.count]}
    />
  );
}
